// /src/components/admin/AdminSideBar.jsx

import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useChatStatusUnread } from '../../hooks/useChatStatusUnread';
import { usePendingStructureChangeRequests } from '../../hooks/usePendingStructureChangeRequests';
import { usePendingInternalApplications } from '../../hooks/usePendingInternalApplications';
import { canTrackApplicationChatUnread } from '../../utils/applicationChatPermissions';
import {
  canTrackInternalApplicationsUnread,
  canTrackStructureChangeRequestsUnread,
} from '../../utils/organisationMembersPermissions';

export default function AdminSideBar({ userHasPermission }) {
  const location = useLocation();
  const { t } = useTranslation();

  const canTrackApplicationsUnread = canTrackApplicationChatUnread(userHasPermission);
  const { hasApplicationsUnread } = useChatStatusUnread({
    enabled: canTrackApplicationsUnread
  });

  const canTrackMembersRequestsUnread = canTrackStructureChangeRequestsUnread(
    userHasPermission
  );
  const canTrackMembersAppsUnread = canTrackInternalApplicationsUnread(userHasPermission);
  const { hasPending: hasPendingStructureChangeRequests } =
    usePendingStructureChangeRequests({
      enabled: canTrackMembersRequestsUnread
    });
  const { hasPending: hasPendingInternalApplications } =
    usePendingInternalApplications({
      enabled: canTrackMembersAppsUnread
    });
  const hasOrganisationMembersUnread =
    (canTrackMembersRequestsUnread && hasPendingStructureChangeRequests) ||
    (canTrackMembersAppsUnread && hasPendingInternalApplications);

  const isActive = (path) => location.pathname.includes(path) ? 'selected' : '';

  return (
    <div className="sidebar-menu glass-card2">
      <div className="desktop-links">
        {userHasPermission("admin.dashboard", "read") &&
        <Link to="/admin/dashboard" className={`desktop-link ${isActive('/admin/dashboard')}`} data-resource="admin.dashboard" data-action="read" data-description="Dashboard">
            {t('Dashboard')}
          </Link>
        }
        {userHasPermission("admin.siteadmin", "write") &&
        <Link to="/admin/siteadmin" className={`desktop-link ${isActive('/admin/siteadmin')}`} data-resource="admin.siteadmin" data-action="write" data-description="Siteadmin">
            {t('Website Admin')}
          </Link>
        }
        {userHasPermission("admin.images", "read") &&
        <Link to="/admin/images" className={`desktop-link ${isActive('/admin/images')}`} data-resource="admin.images" data-action="read" data-description="Images">
            {t('Medias Library')}
          </Link>
        }
        {userHasPermission("admin.onboarding", "read") &&
        <Link to="/admin/onboarding" className={`desktop-link ${isActive('/admin/onboarding')}`} data-resource="admin.onboarding" data-action="read" data-description="Onboarding">
            {t('Onboarding')}
          </Link>
        }
        {userHasPermission("admin.organization", "read") &&
        <Link to="/admin/organization" className={`desktop-link ${isActive('/admin/organization')}`} data-resource="admin.organization" data-action="read" data-description="Organization">
            {t('Organization')}
            {canTrackApplicationsUnread && hasApplicationsUnread &&
            <span className="desktop-link-unread-circle" aria-hidden="true" />
            }
          </Link>
        }
        {userHasPermission("admin.organisationMembers", "read") &&
        <Link to="/admin/organisation-members" className={`desktop-link ${isActive('/admin/organisation-members')}`} data-resource="admin.organisationMembers" data-action="read" data-description="Organisation members">
            {t('Organization members')}
            {hasOrganisationMembersUnread &&
            <span className="desktop-link-unread-circle" aria-hidden="true" />
            }
          </Link>
        }
        {userHasPermission("admin.membersperks", "read") &&
        <Link to="/admin/members-perks" className={`desktop-link ${isActive('/admin/members-perks')}`} data-resource="admin.membersperks" data-action="read" data-description="Members perks">
            {t('Members perks')}
          </Link>
        }
        {userHasPermission("admin.accounting", "read") &&
        <Link to="/admin/accounting" className={`desktop-link ${isActive('/admin/accounting')}`} data-resource="admin.accounting" data-action="read" data-description="Accounting">
            {t('Accounting')}
          </Link>
        }
        {userHasPermission("admin.publicpages", "read") &&
        <Link to="/admin/publicpages" className={`desktop-link ${isActive('/admin/publicpages')}`} data-resource="admin.publicpages" data-action="read" data-description="Publicpages">
            {t('Public Documents')}
          </Link>
        }
        {userHasPermission("admin.privatedocuments", "read") &&
        <Link to="/admin/privatedocuments" className={`desktop-link ${isActive('/admin/privatedocuments')}`} data-resource="admin.privatedocuments" data-action="read" data-description="Privatedocuments">
            {t('Private Documents')}
          </Link>
        }
        {userHasPermission("admin.eventmanagement", "read") &&
        <Link to="/admin/eventmanagement" className={`desktop-link ${isActive('/admin/eventmanagement')}`} data-resource="admin.eventmanagement" data-action="read" data-description="Eventmanagement">
            {t('Events Management')}
          </Link>
        }
        {userHasPermission("admin.orgstructure", "read") &&
        <Link to="/admin/orgstructure" className={`desktop-link ${isActive('/admin/orgstructure')}`} data-resource="admin.orgstructure" data-action="read" data-description="Orgstructure">
            {t('Org Structure')}
          </Link>
        }
        {userHasPermission("admin.academy", "read") &&
        <Link to="/admin/academy" className={`desktop-link ${isActive('/admin/academy')}`} data-resource="admin.academy" data-action="read" data-description="Academy">
            {t('Academy')}
          </Link>
        }
        {userHasPermission("admin.alliances", "read") &&
        <Link to="/admin/alliances" className={`desktop-link ${isActive('/admin/alliances')}`} data-resource="admin.alliances" data-action="read" data-description="Alliances">
            {t('Alliances')}
          </Link>
        }
      </div>
    </div>);

}